import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';

function getDateRange2Years() {
  const end = new Date();
  const start = new Date();
  start.setFullYear(end.getFullYear() - 2);
  const toStr = (d: Date) => d.toISOString().slice(0, 10);
  return { start: toStr(start), end: toStr(end) };
}

// Firestore 문서ID 금지문자 치환 함수(백엔드와 동일하게)
function safeFirestoreId(str: string) {
  return (str || '').replace(/[\/.#$\[\]]/g, '_');
}
function makeRouteKey(company: string, route: string, cargo: string) {
  return safeFirestoreId(`${company}|${route}|${cargo}`);
}

const SearchPage: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState<'vehicle' | 'route' | 'routeList'>('vehicle');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [vehicleSuggestions, setVehicleSuggestions] = useState<string[]>([]);
  const [showVehicleSuggestions, setShowVehicleSuggestions] = useState(false);
  const vehicleSuggestionsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const vehicleInputWrapperRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any[] | null>(null);
  const [searched, setSearched] = useState(false);
  const { start, end } = getDateRange2Years();

  // 운송구간 검색 상태
  const [routeRoute, setRouteRoute] = useState('');
  const [routeResults, setRouteResults] = useState<any[]>([]);
  const [routeTotal, setRouteTotal] = useState(0);
  const [routeSearched, setRouteSearched] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeOffset, setRouteOffset] = useState(0);
  const routeLimit = 20;

  // 운송구간 통계 검색 상태
  const [routeStats, setRouteStats] = useState<any | null>(null);
  const [routeStatsLoading, setRouteStatsLoading] = useState(false);
  const [routeStatsSearched, setRouteStatsSearched] = useState(false);
  const [routeSuggestions, setRouteSuggestions] = useState<string[]>([]);
  const [showRouteSuggestions, setShowRouteSuggestions] = useState(false);
  const routeSuggestionsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const routeInputWrapperRef = useRef<HTMLDivElement>(null);

  // 운송구간 목록 검색 상태 (운임표 대체)
  const [routeListRoute, setRouteListRoute] = useState('');
  const [routeListSuggestions, setRouteListSuggestions] = useState<string[]>([]);
  const [showRouteListSuggestions, setShowRouteListSuggestions] = useState(false);
  const routeListSuggestionsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const routeListInputWrapperRef = useRef<HTMLDivElement>(null);
  const [routeListResult, setRouteListResult] = useState<any[] | null>(null);
  const [routeListLoading, setRouteListLoading] = useState(false);
  const [routeListSearched, setRouteListSearched] = useState(false);

  // ESC 키로 닫기 기능
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // 차량번호 부분 입력 시 자동완성 제안 (디바운스)
  useEffect(() => {
    const q = vehicleNumber.trim();
    if (vehicleSuggestionsTimeoutRef.current) clearTimeout(vehicleSuggestionsTimeoutRef.current);
    if (!q) {
      setVehicleSuggestions([]);
      setShowVehicleSuggestions(false);
      return;
    }
    vehicleSuggestionsTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/settlement-reports/pg/vehicle-suggestions?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        setVehicleSuggestions(Array.isArray(data) ? data : []);
        setShowVehicleSuggestions(Array.isArray(data) && data.length > 0);
      } catch {
        setVehicleSuggestions([]);
        setShowVehicleSuggestions(false);
      }
    }, 300);
    return () => {
      if (vehicleSuggestionsTimeoutRef.current) clearTimeout(vehicleSuggestionsTimeoutRef.current);
    };
  }, [vehicleNumber]);

  // 제안 목록에서 차량번호 선택 시 해당 번호로 설정하고 목록 숨김
  const handleSelectVehicleSuggestion = (num: string) => {
    setVehicleNumber(num);
    setShowVehicleSuggestions(false);
    setVehicleSuggestions([]);
    handleSearch(num);
  };

  // 제안 영역 외부 클릭 시 목록 숨김 (차량번호)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (vehicleInputWrapperRef.current && !vehicleInputWrapperRef.current.contains(e.target as Node)) {
        setShowVehicleSuggestions(false);
      }
      if (routeInputWrapperRef.current && !routeInputWrapperRef.current.contains(e.target as Node)) {
        setShowRouteSuggestions(false);
      }
      if (routeListInputWrapperRef.current && !routeListInputWrapperRef.current.contains(e.target as Node)) {
        setShowRouteListSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = async (overrideVehicle?: string) => {
    const searchVehicle = (overrideVehicle ?? vehicleNumber).trim();
    if (!searchVehicle) return;
    setLoading(true);
    setSearched(true);
    setResult(null);
    try {
      const res = await fetch(`/api/settlement-reports/pg/driver?start=${start}&end=${end}&vehicles=${encodeURIComponent(searchVehicle)}`);
      const data = await res.json();
      const rows = data[searchVehicle] || [];
      // 최근 날짜가 먼저 나오도록 날짜 내림차순 정렬
      rows.sort((a: { date?: string }, b: { date?: string }) => (b.date || '').localeCompare(a.date || ''));
      setResult(rows);
      if (overrideVehicle) setVehicleNumber(overrideVehicle);
    } catch (e) {
      setResult([]);
    }
    setLoading(false);
  };

  // 운송구간 부분 문자열 검색 핸들러
  const handleRouteSearch = async (reset = true) => {
    if (!routeRoute.trim()) return;
    setRouteLoading(true);
    setRouteSearched(true);
    if (reset) {
      setRouteResults([]);
      setRouteOffset(0);
    }
    try {
      const res = await fetch(`/api/route-history-cache/search?route=${encodeURIComponent(routeRoute.trim())}&offset=${reset ? 0 : routeOffset}&limit=${routeLimit}`);
      const data = await res.json();
      if (reset) {
        setRouteResults(data.results || []);
      } else {
        setRouteResults(prev => [...prev, ...(data.results || [])]);
      }
      setRouteTotal(data.total || 0);
      if (reset) setRouteOffset(routeLimit);
      else setRouteOffset(prev => prev + routeLimit);
    } catch (e) {
      if (reset) setRouteResults([]);
    }
    setRouteLoading(false);
  };

  // 더보기 핸들러
  const handleRouteLoadMore = () => {
    handleRouteSearch(false);
  };

  // 운송구간 통계 검색 핸들러 (PostgreSQL, 선택한 구간으로 조회)
  const handleRouteStatsSearch = async (overrideRoute?: string) => {
    const searchRoute = (overrideRoute ?? routeRoute).trim();
    if (!searchRoute) return;
    setRouteStatsLoading(true);
    setRouteStatsSearched(true);
    setRouteStats(null);
    try {
      const res = await fetch(`/api/settlement-reports/pg/route-stats?route=${encodeURIComponent(searchRoute)}`);
      const data = await res.json();
      setRouteStats(data);
      if (overrideRoute) setRouteRoute(overrideRoute);
    } catch (e) {
      setRouteStats(null);
    }
    setRouteStatsLoading(false);
  };

  // 운송구간 부분 입력 시 제안 목록 (디바운스)
  useEffect(() => {
    if (activeTab !== 'route') return;
    const q = routeRoute.trim();
    if (routeSuggestionsTimeoutRef.current) clearTimeout(routeSuggestionsTimeoutRef.current);
    if (!q) {
      setRouteSuggestions([]);
      setShowRouteSuggestions(false);
      return;
    }
    routeSuggestionsTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/settlement-reports/pg/route-suggestions?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        setRouteSuggestions(Array.isArray(data) ? data : []);
        setShowRouteSuggestions(Array.isArray(data) && data.length > 0);
      } catch {
        setRouteSuggestions([]);
        setShowRouteSuggestions(false);
      }
    }, 300);
    return () => {
      if (routeSuggestionsTimeoutRef.current) clearTimeout(routeSuggestionsTimeoutRef.current);
    };
  }, [activeTab, routeRoute]);

  const handleSelectRouteSuggestion = (route: string) => {
    setRouteRoute(route);
    setShowRouteSuggestions(false);
    setRouteSuggestions([]);
    handleRouteStatsSearch(route);
  };

  // 운송구간 목록: 부분 입력 시 제안 (디바운스)
  useEffect(() => {
    if (activeTab !== 'routeList') return;
    const q = routeListRoute.trim();
    if (routeListSuggestionsTimeoutRef.current) clearTimeout(routeListSuggestionsTimeoutRef.current);
    if (!q) {
      setRouteListSuggestions([]);
      setShowRouteListSuggestions(false);
      return;
    }
    routeListSuggestionsTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/settlement-reports/pg/route-suggestions?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        setRouteListSuggestions(Array.isArray(data) ? data : []);
        setShowRouteListSuggestions(Array.isArray(data) && data.length > 0);
      } catch {
        setRouteListSuggestions([]);
        setShowRouteListSuggestions(false);
      }
    }, 300);
    return () => {
      if (routeListSuggestionsTimeoutRef.current) clearTimeout(routeListSuggestionsTimeoutRef.current);
    };
  }, [activeTab, routeListRoute]);

  const handleRouteListSearch = async (overrideRoute?: string) => {
    const searchRoute = (overrideRoute ?? routeListRoute).trim();
    if (!searchRoute) return;
    setRouteListLoading(true);
    setRouteListSearched(true);
    setRouteListResult(null);
    try {
      const res = await fetch(`/api/settlement-reports/pg/route-list?route=${encodeURIComponent(searchRoute)}`);
      const data = await res.json();
      setRouteListResult(Array.isArray(data) ? data : []);
      if (overrideRoute) setRouteListRoute(overrideRoute);
    } catch (e) {
      setRouteListResult([]);
    }
    setRouteListLoading(false);
  };

  const handleSelectRouteListSuggestion = (route: string) => {
    setRouteListRoute(route);
    setShowRouteListSuggestions(false);
    setRouteListSuggestions([]);
    handleRouteListSearch(route);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.25)', zIndex: 9999
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, minWidth: 920, width: '90vw', maxWidth: 1000, minHeight: 560, maxHeight: '90vh',
        boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
        padding: 32, position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        {/* 닫기 버튼 */}
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        {/* 탭 네비게이션 */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          <button
            style={{
              padding: '8px 24px',
              border: 'none',
              borderBottom: activeTab === 'vehicle' ? '3px solid #1976d2' : 'none',
              background: 'none',
              color: activeTab === 'vehicle' ? '#1976d2' : '#888',
              fontWeight: 700,
              fontSize: 17,
              cursor: 'pointer'
            }}
            onClick={() => setActiveTab('vehicle')}
          >
            차량 결제 내역
          </button>
          <button
            style={{
              padding: '8px 24px',
              border: 'none',
              borderBottom: activeTab === 'route' ? '3px solid #1976d2' : 'none',
              background: 'none',
              color: activeTab === 'route' ? '#1976d2' : '#888',
              fontWeight: 700,
              fontSize: 17,
              cursor: 'pointer'
            }}
            onClick={() => setActiveTab('route')}
          >
            운송구간 통계
          </button>
          <button
            style={{
              padding: '8px 24px',
              border: 'none',
              borderBottom: activeTab === 'routeList' ? '3px solid #1976d2' : 'none',
              background: 'none',
              color: activeTab === 'routeList' ? '#1976d2' : '#888',
              fontWeight: 700,
              fontSize: 17,
              cursor: 'pointer'
            }}
            onClick={() => setActiveTab('routeList')}
          >
            운송구간 목록
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {activeTab === 'vehicle' && (
          <>
            {/* 안내 텍스트 */}
            <div style={{ marginBottom: 8, color: '#1976d2', fontWeight: 500, fontSize: 15 }}>
              최근 2년치 데이터만 조회됩니다. 차량번호 일부(예: 4743)를 입력하면 매칭되는 차량 목록이 뜹니다.
            </div>
            {/* 검색 인풋/버튼 + 제안 목록 */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 18, alignItems: 'flex-start' }}>
              <div ref={vehicleInputWrapperRef} style={{ flex: 1, position: 'relative' }}>
                <input
                  type="text"
                  value={vehicleNumber}
                  onChange={e => setVehicleNumber(e.target.value)}
                  onFocus={() => vehicleSuggestions.length > 0 && setShowVehicleSuggestions(true)}
                  placeholder="차량번호 또는 일부 입력 (예: 4743)"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 4, border: '1px solid #bbb', fontSize: 16, boxSizing: 'border-box' }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setShowVehicleSuggestions(false);
                      handleSearch();
                    }
                    if (e.key === 'Escape') setShowVehicleSuggestions(false);
                  }}
                />
                {showVehicleSuggestions && vehicleSuggestions.length > 0 && (
                  <ul
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      margin: 0,
                      padding: 0,
                      listStyle: 'none',
                      background: '#fff',
                      border: '1px solid #bbb',
                      borderRadius: 4,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      maxHeight: 240,
                      overflowY: 'auto',
                      zIndex: 10
                    }}
                  >
                    {vehicleSuggestions.map((num, idx) => (
                      <li
                        key={idx}
                        onClick={() => handleSelectVehicleSuggestion(num)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          fontSize: 15,
                          borderBottom: idx < vehicleSuggestions.length - 1 ? '1px solid #eee' : 'none'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#e8f4fd'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                      >
                        {num}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                onClick={() => handleSearch()}
                style={{ padding: '8px 20px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 600, fontSize: 16, cursor: 'pointer' }}
                disabled={loading || !vehicleNumber.trim()}
              >
                {loading ? '검색중...' : '검색'}
              </button>
            </div>
            {/* 결과 영역 */}
            {searched && !loading && (
              <div>
                <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>
                  차량번호: <span style={{ color: '#1976d2' }}>{vehicleNumber}</span>
                </div>
                <div style={{ fontSize: 14, color: '#888', marginBottom: 8 }}>
                  {start} ~ {end}
                </div>
                {(!result || result.length === 0) ? (
                  <div style={{ color: '#d32f2f', fontWeight: 500, marginTop: 18 }}>
                    {start} ~ {end} 조회결과 데이터없음!
                  </div>
                ) : (
                  <div style={{ maxHeight: 480, overflowY: 'auto', marginTop: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                      <thead>
                        <tr style={{ background: '#f5f7fa' }}>
                          <th style={{ border: '1px solid #bbb', padding: 6 }}>날짜</th>
                          <th style={{ border: '1px solid #bbb', padding: 6 }}>운행구간</th>
                          <th style={{ border: '1px solid #bbb', padding: 6 }}>차종</th>
                          <th style={{ border: '1px solid #bbb', padding: 6 }}>운송비</th>
                          <th style={{ border: '1px solid #bbb', padding: 6 }}>정산상태</th>
                          <th style={{ border: '1px solid #bbb', padding: 6 }}>정산일</th>
                          <th style={{ border: '1px solid #bbb', padding: 6 }}>인수증</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ border: '1px solid #ddd', textAlign: 'center' }}>{row.date}</td>
                            <td style={{ border: '1px solid #ddd', textAlign: 'center' }}>{row.route}</td>
                            <td style={{ border: '1px solid #ddd', textAlign: 'center' }}>{row.cargo}</td>
                            <td style={{ border: '1px solid #ddd', textAlign: 'right', paddingRight: 8 }}>{row.paymentAmount ? Number(row.paymentAmount).toLocaleString() : 0}</td>
                            <td style={{ border: '1px solid #ddd', textAlign: 'center' }}>{row.settlementStatus || ''}</td>
                            <td style={{ border: '1px solid #ddd', textAlign: 'center' }}>{row.settlementDate || ''}</td>
                            <td style={{ border: '1px solid #ddd', textAlign: 'center' }}>{row.Receipt || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {activeTab === 'route' && (
          <>
            <div style={{ marginBottom: 8, color: '#1976d2', fontWeight: 500, fontSize: 15 }}>
              운송구간 일부(예: 평택)를 입력하면 매칭되는 구간 목록이 뜹니다. 목록에서 선택하면 해당 구간의 차종별 통계(최빈/최대/최소값)를 보여줍니다.
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 18, alignItems: 'flex-start' }}>
              <div ref={routeInputWrapperRef} style={{ flex: 1, position: 'relative' }}>
                <input
                  type="text"
                  value={routeRoute}
                  onChange={e => setRouteRoute(e.target.value)}
                  onFocus={() => routeSuggestions.length > 0 && setShowRouteSuggestions(true)}
                  placeholder="운송구간 또는 일부 입력 (예: 평택)"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 4, border: '1px solid #bbb', fontSize: 16, boxSizing: 'border-box' }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setShowRouteSuggestions(false);
                      handleRouteStatsSearch();
                    }
                    if (e.key === 'Escape') setShowRouteSuggestions(false);
                  }}
                />
                {showRouteSuggestions && routeSuggestions.length > 0 && (
                  <ul
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      margin: 0,
                      padding: 0,
                      listStyle: 'none',
                      background: '#fff',
                      border: '1px solid #bbb',
                      borderRadius: 4,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      maxHeight: 240,
                      overflowY: 'auto',
                      zIndex: 10
                    }}
                  >
                    {routeSuggestions.map((r, idx) => (
                      <li
                        key={idx}
                        onClick={() => handleSelectRouteSuggestion(r)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          fontSize: 15,
                          borderBottom: idx < routeSuggestions.length - 1 ? '1px solid #eee' : 'none'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#e8f4fd'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                      >
                        {r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                onClick={() => handleRouteStatsSearch()}
                style={{ padding: '8px 20px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 600, fontSize: 16, cursor: 'pointer' }}
                disabled={routeStatsLoading || !routeRoute.trim()}
              >
                {routeStatsLoading ? '검색중...' : '검색'}
              </button>
            </div>
            {/* 결과 영역: 차종별 통계 표 */}
            {routeStatsSearched && !routeStatsLoading && (
              <div style={{ marginTop: 8 }}>
                {!routeStats || !routeStats.stats || Object.keys(routeStats.stats).length === 0 ? (
                  <div style={{ color: '#d32f2f', fontWeight: 500, marginTop: 18 }}>
                    조회결과 데이터없음!
                  </div>
                ) : (
                  <div style={{ maxWidth: 700, margin: '0 auto', background: '#fff', borderRadius: 8, padding: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                      <thead>
                        <tr style={{ background: '#f5f7fa' }}>
                          <th style={{ padding: 6, borderBottom: '1px solid #ddd' }}>운송구간</th>
                          <th style={{ padding: 6, borderBottom: '1px solid #ddd' }}>차종</th>
                          <th style={{ padding: 6, borderBottom: '1px solid #ddd', background: '#f5f5f5', color: '#888', fontWeight: 700 }}>청구최빈값</th>
                          <th style={{ padding: 6, borderBottom: '1px solid #ddd', background: '#f5f5f5', color: '#888', fontWeight: 700 }}>지불최빈값</th>
                          <th style={{ padding: 6, borderBottom: '1px solid #ddd', background: '#ffeaea', color: '#d32f2f' }}>청구최대값</th>
                          <th style={{ padding: 6, borderBottom: '1px solid #ddd', background: '#ffeaea', color: '#d32f2f' }}>지불최대값</th>
                          <th style={{ padding: 6, borderBottom: '1px solid #ddd', background: '#eaf3ff', color: '#1976d2' }}>청구최소값</th>
                          <th style={{ padding: 6, borderBottom: '1px solid #ddd', background: '#eaf3ff', color: '#1976d2' }}>지불최소값</th>
                          <th style={{ padding: 6, borderBottom: '1px solid #ddd' }}>건수</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(routeStats.stats).map(([cargo, stat]: any) => (
                          <tr key={cargo}>
                            <td style={{ padding: 6, borderBottom: '1px solid #eee' }}>{routeStats.route}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eee' }}>{cargo}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eee', textAlign: 'right', background: '#f5f5f5', color: '#888', fontWeight: 700 }}>{stat.billingMode !== null && stat.billingMode !== undefined ? Number(stat.billingMode).toLocaleString() : ''}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eee', textAlign: 'right', background: '#f5f5f5', color: '#888', fontWeight: 700 }}>{stat.paymentMode !== null && stat.paymentMode !== undefined ? Number(stat.paymentMode).toLocaleString() : ''}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eee', textAlign: 'right', background: '#ffeaea', color: '#d32f2f' }}>{stat.billingMax !== null && stat.billingMax !== undefined ? Number(stat.billingMax).toLocaleString() : ''}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eee', textAlign: 'right', background: '#ffeaea', color: '#d32f2f' }}>{stat.paymentMax !== null && stat.paymentMax !== undefined ? Number(stat.paymentMax).toLocaleString() : ''}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eee', textAlign: 'right', background: '#eaf3ff', color: '#1976d2' }}>{stat.billingMin !== null && stat.billingMin !== undefined ? Number(stat.billingMin).toLocaleString() : ''}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eee', textAlign: 'right', background: '#eaf3ff', color: '#1976d2' }}>{stat.paymentMin !== null && stat.paymentMin !== undefined ? Number(stat.paymentMin).toLocaleString() : ''}</td>
                            <td style={{ padding: 6, borderBottom: '1px solid #eee', textAlign: 'center' }}>{stat.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {activeTab === 'routeList' && (
          <>
            <div style={{ marginBottom: 8, color: '#1976d2', fontWeight: 500, fontSize: 15 }}>
              최근 2년치 데이터입니다. 운송구간 일부(예: 대전)를 입력하면 매칭되는 구간 목록이 뜹니다. 목록에서 선택하면 해당 구간의 전체 행 목록을 보여줍니다.
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 18, alignItems: 'flex-start' }}>
              <div ref={routeListInputWrapperRef} style={{ flex: 1, position: 'relative' }}>
                <input
                  type="text"
                  value={routeListRoute}
                  onChange={e => setRouteListRoute(e.target.value)}
                  onFocus={() => routeListSuggestions.length > 0 && setShowRouteListSuggestions(true)}
                  placeholder="운송구간 또는 일부 입력 (예: 대전)"
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 4, border: '1px solid #bbb', fontSize: 16, boxSizing: 'border-box' }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setShowRouteListSuggestions(false);
                      handleRouteListSearch();
                    }
                    if (e.key === 'Escape') setShowRouteListSuggestions(false);
                  }}
                />
                {showRouteListSuggestions && routeListSuggestions.length > 0 && (
                  <ul
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      margin: 0,
                      padding: 0,
                      listStyle: 'none',
                      background: '#fff',
                      border: '1px solid #bbb',
                      borderRadius: 4,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      maxHeight: 240,
                      overflowY: 'auto',
                      zIndex: 10
                    }}
                  >
                    {routeListSuggestions.map((r, idx) => (
                      <li
                        key={idx}
                        onClick={() => handleSelectRouteListSuggestion(r)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          fontSize: 15,
                          borderBottom: idx < routeListSuggestions.length - 1 ? '1px solid #eee' : 'none'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#e8f4fd'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                      >
                        {r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                onClick={() => handleRouteListSearch()}
                style={{ padding: '8px 20px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 600, fontSize: 16, cursor: 'pointer' }}
                disabled={routeListLoading || !routeListRoute.trim()}
              >
                {routeListLoading ? '검색중...' : '검색'}
              </button>
            </div>
            {routeListSearched && !routeListLoading && (
              <div style={{ marginTop: 8 }}>
                {!routeListResult || routeListResult.length === 0 ? (
                  <div style={{ color: '#d32f2f', fontWeight: 500, marginTop: 18 }}>
                    조회결과 데이터없음!
                  </div>
                ) : (
                  <div style={{ maxHeight: 480, overflowY: 'auto', marginTop: 8 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15, tableLayout: 'fixed' }}>
                      <thead>
                        <tr style={{ background: '#e8eef4' }}>
                          <th style={{ border: '1px solid #999', padding: '8px 10px', textAlign: 'left', width: 110 }}>날짜</th>
                          <th style={{ border: '1px solid #999', padding: '8px 10px', textAlign: 'left' }}>운송구간</th>
                          <th style={{ border: '1px solid #999', padding: '8px 10px', textAlign: 'center', width: 80 }}>차종</th>
                          <th style={{ border: '1px solid #999', padding: '8px 10px', textAlign: 'center', width: 100 }}>차량번호</th>
                          <th style={{ border: '1px solid #999', padding: '8px 10px', textAlign: 'right', width: 110 }}>청구운송비</th>
                          <th style={{ border: '1px solid #999', padding: '8px 10px', textAlign: 'right', width: 110 }}>지불운송비</th>
                        </tr>
                      </thead>
                      <tbody>
                        {routeListResult.map((row, idx) => (
                          <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ border: '1px solid #ccc', padding: '8px 10px', verticalAlign: 'middle' }}>{row.date || ''}</td>
                            <td style={{ border: '1px solid #ccc', padding: '8px 10px', verticalAlign: 'middle' }}>{row.route || ''}</td>
                            <td style={{ border: '1px solid #ccc', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle' }}>{row.cargo}</td>
                            <td style={{ border: '1px solid #ccc', padding: '8px 10px', textAlign: 'center', verticalAlign: 'middle' }}>{row.vehicleNumber}</td>
                            <td style={{ border: '1px solid #ccc', padding: '8px 10px', textAlign: 'right', verticalAlign: 'middle' }}>{row.billingAmount != null ? Number(row.billingAmount).toLocaleString() : ''}</td>
                            <td style={{ border: '1px solid #ccc', padding: '8px 10px', textAlign: 'right', verticalAlign: 'middle' }}>{row.paymentAmount != null ? Number(row.paymentAmount).toLocaleString() : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default SearchPage; 